import type { Chest, SpecialChest } from "@/game/entities/objects/chests";
import type { Timestamp } from "@/types";
import {
	createImmutableState,
	getImmutableState,
	getImmutableStateProperty,
	setImmutableState,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore } from "pinia";
import { type ComputedRef } from "vue";

export type ChestStoreState = {
	activeChests: Array<Chest>;
	claimedChests: Array<Chest>;
	exploredToday: number;
	specialChestCooldownUntil: number;
	activeSpecialChest: SpecialChest | null;
	lastSaveDate: string; // ISO date string (YYYY-MM-DD)
};

type ChestStoreDiffAction = {
	// type: "set";
	key: keyof ChestStoreState;
	currentValue: ChestStoreState[keyof ChestStoreState];
	value: ChestStoreState[keyof ChestStoreState];
	// reason?: string | false;
	timestamp: Timestamp;
};

type ChestStore = {
	$getPayload: () => ChestStorePayload | null;
	$resetPayload: () => void;
	$setState: (value: Partial<ChestStoreState>) => void;
	activeChests: ComputedRef<Array<Chest>>;
	claimedChests: ComputedRef<Array<Chest>>;
	exploredToday: ComputedRef<number>;
	specialChestCooldownUntil: ComputedRef<number>;
	activeSpecialChest: ComputedRef<SpecialChest | null>;
	lastSaveDate: ComputedRef<string>;
	setActiveChests(chests: Array<Chest>): void;
	addActiveChest(chest: Chest): void;
	setClaimedChests(chests: Array<Chest>): void;
	addClaimedChest(chest: Chest): void;
	setExploredToday(count: number): void;
	incrementExploredToday(increment: number): void;
	setSpecialChestCooldownUntil(timestamp: number): void;
	setActiveSpecialChest(chest: SpecialChest | null): void;
	setLastSaveDate(date: string): void;
	checkAndResetIfNewDay(): boolean;
};

type ChestStorePayload = {
	state: ChestStoreState;
	actions: Array<ChestStoreDiffAction>;
};

const STORE_NAME = "chest";

export default defineStore(STORE_NAME, (): ChestStore => {
	// Immutable state can never be changed directly,
	// but can be replaced with a new version of the state containing the desired changes.
	const state = createImmutableState<ChestStoreState>({
		activeChests: [],
		claimedChests: [],
		exploredToday: 0,
		specialChestCooldownUntil: 0,
		activeSpecialChest: null,
		lastSaveDate: new Date().toISOString().split("T")[0], // Today's date in YYYY-MM-DD format
	});

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getState = (): ChestStoreState => {
		const data = getImmutableState(state).value;
		const keys = Object.keys(data) as Array<keyof ChestStoreState>;
		return keys.reduce(
			(
				acc: ChestStoreState,
				key: keyof ChestStoreState,
			): ChestStoreState => ({
				...acc,
				[key]: data[key],
			}),
			{} as ChestStoreState,
		);
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $actions = new Array<ChestStoreDiffAction>();

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getActions = (): Array<ChestStoreDiffAction> =>
		$actions.splice(0, $actions.length);

	// Used only for save.
	// DO NOT USE IN GAME!
	let $isPatched = false;

	// Used only for save.
	// DO NOT USE IN GAME!
	let $payloadInProgress: ChestStorePayload | null = null;

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getPayload = (): ChestStorePayload | null => {
		if (!$isPatched) return null;
		if ($payloadInProgress !== null) return $payloadInProgress;

		return ($payloadInProgress = {
			state: $getState(),
			actions: $getActions(),
		});
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $resetPayload = (): void => {
		$payloadInProgress = null;
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $setState = (value: Partial<ChestStoreState>): void => {
		setImmutableState(state, value);
		setTimeout((): void => {
			$isPatched = true;
		}, 0);
	};

	const activeChests = getImmutableStateProperty(state, "activeChests", []);

	const claimedChests = getImmutableStateProperty(state, "claimedChests", []);

	const exploredToday = getImmutableStateProperty(state, "exploredToday", 0);

	const specialChestCooldownUntil = getImmutableStateProperty(
		state,
		"specialChestCooldownUntil",
		0,
	);

	const activeSpecialChest = getImmutableStateProperty(
		state,
		"activeSpecialChest",
		null,
	);

	const lastSaveDate = getImmutableStateProperty(
		state,
		"lastSaveDate",
		new Date().toISOString().split("T")[0],
	);

	const setActiveChests = (chests: Array<Chest>): void => {
		const currentValue = state.value.activeChests;
		setImmutableStateProperty(state, "activeChests", chests);
		$actions.push({
			key: "activeChests",
			currentValue,
			value: chests,
			timestamp: Date.now(),
		});
	};

	const addActiveChest = (chest: Chest): void => {
		const currentValue = state.value.activeChests;
		const newValue = [...currentValue, chest];
		setImmutableStateProperty(state, "activeChests", newValue);
		$actions.push({
			key: "activeChests",
			currentValue,
			value: newValue,
			timestamp: Date.now(),
		});
	};

	const setClaimedChests = (chests: Array<Chest>): void => {
		const currentValue = state.value.claimedChests;
		setImmutableStateProperty(state, "claimedChests", chests);
		$actions.push({
			key: "claimedChests",
			currentValue,
			value: chests,
			timestamp: Date.now(),
		});
	};

	const addClaimedChest = (chest: Chest): void => {
		const currentValue = state.value.claimedChests;
		const newValue = [...currentValue, chest];
		setImmutableStateProperty(state, "claimedChests", newValue);
		$actions.push({
			key: "claimedChests",
			currentValue,
			value: newValue,
			timestamp: Date.now(),
		});
	};

	const setExploredToday = (count: number): void => {
		const currentValue = state.value.exploredToday;
		setImmutableStateProperty(state, "exploredToday", count);
		$actions.push({
			key: "exploredToday",
			currentValue,
			value: count,
			timestamp: Date.now(),
		});
	};

	const incrementExploredToday = (increment: number): void => {
		const currentValue = state.value.exploredToday;
		const newValue = currentValue + increment;
		setImmutableStateProperty(state, "exploredToday", newValue);
		$actions.push({
			key: "exploredToday",
			currentValue,
			value: newValue,
			timestamp: Date.now(),
		});
	};

	const setSpecialChestCooldownUntil = (timestamp: number): void => {
		const currentValue = state.value.specialChestCooldownUntil;
		setImmutableStateProperty(
			state,
			"specialChestCooldownUntil",
			timestamp,
		);
		$actions.push({
			key: "specialChestCooldownUntil",
			currentValue,
			value: timestamp,
			timestamp: Date.now(),
		});
	};

	const setActiveSpecialChest = (chest: SpecialChest | null): void => {
		const currentValue = state.value.activeSpecialChest;
		setImmutableStateProperty(state, "activeSpecialChest", chest);
		$actions.push({
			key: "activeSpecialChest",
			currentValue,
			value: chest,
			timestamp: Date.now(),
		});
	};

	const setLastSaveDate = (date: string): void => {
		const currentValue = state.value.lastSaveDate;
		setImmutableStateProperty(state, "lastSaveDate", date);
		$actions.push({
			key: "lastSaveDate",
			currentValue,
			value: date,
			timestamp: Date.now(),
		});
	};

	const checkAndResetIfNewDay = (): boolean => {
		const currentDate = new Date().toISOString().split("T")[0];
		const savedDate = state.value.lastSaveDate;

		if (savedDate !== currentDate) {
			// Reset daily values if it's a new day
			setClaimedChests([]);
			setExploredToday(0);
			setLastSaveDate(currentDate);
			return true; // Indicates that a reset occurred
		}

		return false; // No reset needed
	};

	return {
		$getPayload,
		$resetPayload,
		$setState,
		activeChests,
		claimedChests,
		exploredToday,
		specialChestCooldownUntil,
		activeSpecialChest,
		lastSaveDate,
		setActiveChests,
		addActiveChest,
		setClaimedChests,
		addClaimedChest,
		setExploredToday,
		incrementExploredToday,
		setSpecialChestCooldownUntil,
		setActiveSpecialChest,
		setLastSaveDate,
		checkAndResetIfNewDay,
	};
});
